import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

/* ────────────────────────────────────────────────────────────
 * VerticalTabs — Reusable vertical tab navigation component
 * Inspired by Fresha Partners' drawer multi-section pattern.
 * Supports badges, icons, expandable sub-items, and responsive
 * horizontal mode for mobile viewports.
 * ──────────────────────────────────────────────────────────── */

export interface VerticalTab {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  badge?: number | string;
  children?: VerticalTab[];
}

interface VerticalTabsProps {
  tabs: VerticalTab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  /** Optional section dividers — tab ids after which a divider is rendered */
  sectionAfter?: string[];
  /** Optional section headers that appear before a tab id */
  sectionHeaders?: Record<string, string>;
  className?: string;
}

export function VerticalTabs({
  tabs,
  activeTab,
  onTabChange,
  sectionAfter = [],
  sectionHeaders = {},
  className = '',
}: VerticalTabsProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggleExpand = (tabId: string) => {
    setExpanded(prev => ({ ...prev, [tabId]: !prev[tabId] }));
  };

  const renderTab = (tab: VerticalTab, depth = 0) => {
    const isActive = activeTab === tab.id;
    const hasChildren = tab.children && tab.children.length > 0;
    const isExpanded = expanded[tab.id] ?? true;
    const Icon = tab.icon;

    return (
      <React.Fragment key={tab.id}>
        {/* Section header before this tab */}
        {sectionHeaders[tab.id] && (
          <div className="px-3 pt-4 pb-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
              {sectionHeaders[tab.id]}
            </span>
          </div>
        )}

        <button
          onClick={() => {
            if (hasChildren) {
              toggleExpand(tab.id);
              onTabChange(tab.id);
            } else {
              onTabChange(tab.id);
            }
          }}
          className={`
            w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium rounded-lg
            transition-all duration-200 cursor-pointer group text-left
            ${depth > 0 ? 'pl-8' : ''}
            ${isActive
              ? 'bg-brand-orange/10 text-brand-orange border-l-2 border-brand-orange'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-tertiary/60 border-l-2 border-transparent'
            }
          `}
        >
          {Icon && (
            <Icon className={`h-4 w-4 shrink-0 ${isActive ? 'text-brand-orange' : 'text-text-tertiary group-hover:text-text-secondary'}`} />
          )}

          <span className="flex-1 truncate">{tab.label}</span>

          {tab.badge !== undefined && (
            <span className={`
              text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center
              ${isActive ? 'bg-brand-orange/20 text-brand-orange' : 'bg-surface-tertiary text-text-tertiary'}
            `}>
              {tab.badge}
            </span>
          )}

          {hasChildren && (
            <span className="text-text-tertiary">
              {isExpanded
                ? <ChevronDown className="h-3.5 w-3.5" />
                : <ChevronRight className="h-3.5 w-3.5" />
              }
            </span>
          )}
        </button>

        {/* Expanded children */}
        {hasChildren && isExpanded && (
          <div className="space-y-0.5">
            {tab.children!.map(child => renderTab(child, depth + 1))}
          </div>
        )}

        {/* Section divider after this tab */}
        {sectionAfter.includes(tab.id) && (
          <div className="my-2 border-t border-border/50" />
        )}
      </React.Fragment>
    );
  };

  const activeParentWithChildren = tabs.find(
    (t) => t.id === activeTab || (t.children && t.children.some((c) => c.id === activeTab))
  );

  return (
    <>
      {/* Desktop: Vertical tabs */}
      <nav className={`hidden md:flex flex-col space-y-0.5 min-w-[180px] ${className}`}>
        {tabs.map(tab => renderTab(tab))}
      </nav>

      {/* Mobile: Horizontal scrollable pills with sub-tabs row */}
      <div className={`md:hidden flex flex-col gap-2 ${className}`}>
        <nav className="flex gap-2 overflow-x-auto pb-1 px-1 scrollbar-hide">
          {tabs.map(tab => {
            const isTabDirectlyActive = activeTab === tab.id;
            const isChildActive = tab.children && tab.children.some(c => c.id === activeTab);
            const isActive = isTabDirectlyActive || isChildActive;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onTabChange(tab.id)}
                className={`
                  flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl whitespace-nowrap
                  transition-all duration-200 cursor-pointer shrink-0 border
                  ${isActive
                    ? 'bg-brand-orange text-white border-brand-orange shadow-sm shadow-brand-orange/20'
                    : 'bg-surface-secondary text-text-secondary border-border hover:bg-surface-hover'
                  }
                `}
              >
                {Icon && <Icon className="h-3.5 w-3.5" />}
                <span>{tab.label}</span>
                {tab.badge !== undefined && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-extrabold ${isActive ? 'bg-white/25 text-white' : 'bg-surface text-text-tertiary'}`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Sub-categories row for active section */}
        {activeParentWithChildren && activeParentWithChildren.children && activeParentWithChildren.children.length > 0 && (
          <div className="flex gap-1.5 overflow-x-auto pb-1 px-1 scrollbar-hide animate-fade-in">
            {activeParentWithChildren.children.map((child) => {
              const isChildActive = activeTab === child.id;
              return (
                <button
                  key={child.id}
                  type="button"
                  onClick={() => onTabChange(child.id)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-lg whitespace-nowrap transition-all cursor-pointer shrink-0 border ${
                    isChildActive
                      ? 'bg-brand-orange/15 text-brand-orange border-brand-orange/40 font-bold'
                      : 'bg-surface-secondary/70 text-text-secondary border-border/60 hover:bg-surface-hover'
                  }`}
                >
                  <span>{child.label}</span>
                  {child.badge !== undefined && (
                    <span className={`text-[10px] px-1 rounded-md ${isChildActive ? 'bg-brand-orange text-white' : 'bg-surface text-text-tertiary'}`}>
                      {child.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
